import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/i18n/LanguageContext";
import PWAInstall from "@/components/PWAInstall";
import ErrorBoundary from "@/components/ErrorBoundary";

import Landing from "@/pages/Landing";
import Vendor from "@/pages/Vendor";
import ShowcaseInochi from "@/pages/showcase/Inochi";
import AuthCallback from "@/pages/AuthCallback";
import RoleSelect from "@/pages/RoleSelect";
import PersonaLanding from "@/pages/PersonaLanding";
import OpportunityTV from "@/pages/OpportunityTV";
import PostPromo from "@/pages/PostPromo";
import MembershipPlans from "@/pages/MembershipPlans";
import MyPayments from "@/pages/MyPayments";
import ArtistSignup from "@/pages/ArtistSignup";
import OpportunityDetail from "@/pages/OpportunityDetail";
import OpportunitiesDirectory from "@/pages/OpportunitiesDirectory";
import SuccessStories from "@/pages/SuccessStories";
import Leaderboard from "@/pages/Leaderboard";
import JourneyDetail from "@/pages/JourneyDetail";

import Dashboard from "@/pages/Dashboard";
import Opportunities from "@/pages/Opportunities";
import Applications from "@/pages/Applications";
import Territories from "@/pages/TerritoryMap"; // file now contains the new Territories board
import Notifications from "@/pages/Notifications";
import Membership from "@/pages/Membership";
import Portal from "@/pages/Portal";
import Influencers from "@/pages/Influencers";

import Admin from "@/pages/Admin";
import Profile from "@/pages/Profile";
import Layout from "@/components/Layout";

function ProtectedRoute({ children, adminOnly = false, requireRole = true }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="auth-checking">
        <div className="font-mono text-sm text-zinc-500">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  if (requireRole && !user.role && !user.is_admin) return <Navigate to="/welcome" replace />;
  if (adminOnly && !user.is_admin) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/vendor" element={<Vendor />} />
      <Route path="/showcase/inochi" element={<ShowcaseInochi />} />
      <Route path="/opportunity/:slug" element={<OpportunityDetail />} />
      <Route path="/opportunities-directory" element={<OpportunitiesDirectory />} />
      <Route path="/success-stories" element={<SuccessStories />} />
      <Route path="/leaderboard" element={<Leaderboard />} />
      <Route path="/journey/:slug" element={<JourneyDetail />} />
      <Route path="/for/:slug" element={<PersonaLanding />} />
      <Route path="/tv" element={<OpportunityTV />} />
      <Route path="/membership" element={<MembershipPlans />} />
      <Route path="/artist/signup" element={<ArtistSignup />} />
      <Route path="/welcome" element={
        <ProtectedRoute requireRole={false}>
          <RoleSelect />
        </ProtectedRoute>
      } />

      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/opportunities" element={<Opportunities />} />
        <Route path="/applications" element={<Applications />} />
        <Route path="/territories" element={<Territories />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/membership" element={<Membership />} />
        <Route path="/influencers" element={<Influencers />} />
        <Route path="/portal/:role" element={<Portal />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/post-promo" element={<PostPromo />} />
        <Route path="/payments" element={<MyPayments /> } />
        <Route path="/admin" element={
          <ProtectedRoute adminOnly>
            <ErrorBoundary>
              <Admin />
            </ErrorBoundary>
          </ProtectedRoute>
        } />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ErrorBoundary>
          <AuthProvider>
            <LanguageProvider>
              <PWAInstall />
              <Toaster position="top-right" richColors />
              <AppRouter />
            </LanguageProvider>
          </AuthProvider>
        </ErrorBoundary>
      </BrowserRouter>
    </div>
  );
}

export default App;
